import Button from "@edaz/edazkit/Button/index";
import { IconType } from "@edaz/edazkit/Icon";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import type { ViewComponent } from "@frui.ts/views";
import { preventDefault, registerView } from "@frui.ts/views";
import { startOfEDAZ } from "config";
import DatePickerBox from "controls/datePickerBox";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import { subDays } from "date-fns";
import type AdhocReportRequestResponse from "entities/adhocReportRequestResponse";
import ReportRequestStatus from "entities/reportRequestStatus";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import { busyIdentificator } from "utils/busyWatcher";
import { disabledDaysRange } from "utils/dateUtils";
import { formatDate, formatDateTime, translateEnum, translateGeneral } from "utils/localizations";
import AdHocReportsListViewModel from "viewModels/timeCharges/reporting/adHocReports/adHocReportsListViewModel";
import ListView from "views/common/list";

const AdHocReportsListView: ViewComponent<AdHocReportsListViewModel> = observer(({ vm }) => {
  const isDownloadBusy = vm.busyWatcher.checkBusy(busyIdentificator([vm.request]));

  const columns: ColumnDefinition<AdhocReportRequestResponse>[] = [
    {
      title: vm.translateAttribute("id"),
      property: "id",
    },
    {
      title: vm.translateAttribute("fromDate"),
      valueFormatter: props => formatDate(props.value),
      property: "fromDate",
    },
    {
      title: vm.translateAttribute("toDate"),
      valueFormatter: props => formatDate(props.value),
      property: "toDate",
    },
    {
      title: vm.translateAttribute("lastRefreshedAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "lastRefreshedAt",
    },
    {
      title: vm.translateAttribute("processedAt"),
      valueFormatter: props => formatDateTime(props.value),
      property: "processedAt",
    },
    {
      title: vm.translateAttribute("createdBy"),
      property: "createdBy",
    },
    {
      title: vm.translateAttribute("status"),
      property: "status",
      valueFormatter: props => translateEnum("reportRequestStatus", props.value),
    },
  ];

  const actionButtons = (item: AdhocReportRequestResponse) => {
    const isBusy = vm.busyWatcher.checkBusy(busyIdentificator([item]));
    return (
      <>
        {item.status === ReportRequestStatus.Processed && (
          <Button
            variant="success"
            size="sm"
            className="rounded"
            onClick={() => vm.onDownload(item)}
            disabled={isBusy}
            iconLeft={isBusy ? IconType.Spinner : IconType.Download}>
            {vm.translate("downloadFile")}
          </Button>
        )}
      </>
    );
  };

  const filters = (
    <Form className="bg-light mb-4 p-4 rounded" onSubmit={preventDefault(vm.onFormSubmit)}>
      <Row>
        <FormGroup as={Col} sm={6} md={4} target={vm.request} property="fromDate">
          <FormLabel>{vm.translate("fromDate")}</FormLabel>
          <DatePickerBox
            disabledDays={disabledDaysRange(subDays(new Date(), 1), startOfEDAZ)}
            placeholder={translateGeneral("choose")}
            fromMonth={startOfEDAZ}
            toMonth={new Date()}
            hideToday
          />
        </FormGroup>
        <FormGroup as={Col} sm={6} md={4} target={vm.request} property="toDate">
          <FormLabel>{vm.translate("toDate")}</FormLabel>
          <DatePickerBox
            disabledDays={disabledDaysRange(subDays(new Date(), 1), startOfEDAZ)}
            placeholder={translateGeneral("choose")}
            endOfTheDay={true}
            fromMonth={startOfEDAZ}
            toMonth={new Date()}
            hideToday
          />
        </FormGroup>
        <Col md="auto">
          <Button
            className="d-block mt-md-5 rounded"
            type="submit"
            disabled={isDownloadBusy}
            iconLeft={isDownloadBusy ? IconType.Spinner : undefined}>
            {translateGeneral("generateReport")}
          </Button>
        </Col>
      </Row>
    </Form>
  );

  return (
    <ListView
      vm={vm}
      columns={columns}
      showDetailWithList={true}
      aboveTable={vm.canMakeReport ? filters : null}
      actionButtons={actionButtons}
    />
  );
});

registerView(AdHocReportsListView, AdHocReportsListViewModel);
